/**
 * @description Domain-aware fund link utilities for chiefintech.capital.
 *
 * On the standalone capital domain, fund pages are served at public URLs
 * (e.g. /fund/thesis instead of /capital/fund/thesis). This module provides
 * helpers to transform hrefs so navigation links render the correct path
 * depending on which domain the app is running on.
 *
 * SSR-safe: defaults to prefixed paths when `window` is unavailable.
 */
import { useBrowserLocation } from "wouter/use-browser-location";
import {
  isCapitalDomain,
  getCompanionSubdomain,
  normalizeCompanionPath,
  companionHref,
} from "@/lib/domainUtils";
import { isStartupBenefitsPublicPath } from "@shared/startupBenefitsPublic";

// Re-export so existing imports from fundLinks keep working
export { isCapitalDomain };

const FUND_PREFIX = "/capital/fund";
const SHORT_PREFIX = "/fund";
const CAPITAL_EVENTS_PATH = "/capital/fund/events";
const CAPITAL_EVENTS_PUBLIC_PATH = "/events";
const CAPITAL_CANONICAL_ORIGIN = "https://chiefintech.capital";

export function startupBenefitsCanonicalHref(
  hostname: string,
  pathname: string,
  search = "",
  hash = "",
): string | null {
  const normalizedHost = hostname.trim().toLowerCase();
  if (
    (normalizedHost === "chiefintech.com" || normalizedHost === "www.chiefintech.com")
    && isStartupBenefitsPublicPath(pathname)
  ) {
    return `${CAPITAL_CANONICAL_ORIGIN}${pathname}${search}${hash}`;
  }
  return null;
}

/**
 * @description Transforms a fund href for the current domain context.
 *
 * On chiefintech.capital:
 *   "/capital/fund"           → "/fund"
 *   "/capital/fund/thesis"    → "/fund/thesis"
 *   "/capital/fund/lp"        → "/fund/lp"
 *   "/capital/fund/events"    → "/events"
 *
 * On chiefintech.com (or SSR):
 *   All paths pass through unchanged.
 *
 * Non-fund paths (e.g. "/capital/dealroom") are never modified.
 */
export function fundHref(href: string): string {
  if (!isCapitalDomain()) return href;
  if (href === CAPITAL_EVENTS_PATH) return CAPITAL_EVENTS_PUBLIC_PATH;
  if (href === FUND_PREFIX || href === FUND_PREFIX + "/") return SHORT_PREFIX;
  if (href.startsWith(FUND_PREFIX + "/")) {
    return SHORT_PREFIX + href.slice(FUND_PREFIX.length);
  }
  // Non-fund path — leave unchanged
  return href;
}

/**
 * @description Normalizes a browser location path for fund-route matching.
 * On chiefintech.capital, "/fund/thesis" should match against "/capital/fund/thesis".
 * "/" (apex) is also treated as the fund overview.
 * Other paths are left untouched so the SPA can render its 404.
 */
export function normalizeFundPath(locationPath: string): string {
  if (!isCapitalDomain()) return locationPath;
  if (locationPath.startsWith(FUND_PREFIX)) return locationPath;
  if (
    locationPath === CAPITAL_EVENTS_PUBLIC_PATH ||
    locationPath === CAPITAL_EVENTS_PUBLIC_PATH + "/"
  ) {
    return CAPITAL_EVENTS_PATH;
  }
  if (locationPath === "/" || locationPath === SHORT_PREFIX || locationPath === SHORT_PREFIX + "/") {
    return FUND_PREFIX;
  }
  if (locationPath.startsWith(SHORT_PREFIX + "/")) {
    return FUND_PREFIX + locationPath.slice(SHORT_PREFIX.length);
  }
  return locationPath;
}

/**
 * @description Custom wouter location hook that makes the SPA router
 * match fund routes on chiefintech.capital while keeping clean URLs in
 * the address bar.
 *
 * - On capital domain: routing matches against the normalized
 *   `/capital/fund/...` path (so `<Route path="/capital/fund/thesis">`
 *   matches when the browser URL is `/fund/thesis`), and navigation calls are
 *   transformed back to clean form before being pushed.
 * - On any other domain: behaves exactly like the default browser
 *   location hook — no transformation.
 */
export function useCapitalAwareLocation(): [string, (to: string, opts?: { replace?: boolean }) => void] {
  const [loc, navigate] = useBrowserLocation();

  // Capital domain (chiefintech.capital): match against /capital/fund/* paths.
  if (isCapitalDomain()) {
    const normalizedLoc = normalizeFundPath(loc);
    const wrappedNavigate = (to: string, opts?: { replace?: boolean }) => {
      navigate(fundHref(to), opts);
    };
    return [normalizedLoc, wrappedNavigate];
  }

  // Companion subdomains ({slug}.chiefinai.com): match against /companion/{slug}/*
  // paths while keeping clean URLs in the address bar.
  if (getCompanionSubdomain()) {
    const normalizedLoc = normalizeCompanionPath(loc);
    const wrappedNavigate = (to: string, opts?: { replace?: boolean }) => {
      navigate(companionHref(to), opts);
    };
    return [normalizedLoc, wrappedNavigate];
  }

  return [loc, navigate as (to: string, opts?: { replace?: boolean }) => void];
}
